
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/User/Avatar";
import { MoreHorizontal, Shield, ShieldAlert, Clock } from "lucide-react";
import { GroupMember, GroupRole } from "@/lib/types";

interface MembersListItemProps {
  member: GroupMember;
  currentUserRole: GroupRole;
  currentUserId: string;
  onPromote: () => void;
  onDemote: () => void;
  onBan: () => void;
  onUnban: () => void;
  onMute: () => void;
  onUnmute: () => void;
  onKick: () => void;
}

export function MembersListItem({
  member,
  currentUserRole,
  currentUserId,
  onPromote,
  onDemote,
  onBan,
  onUnban,
  onMute,
  onUnmute,
  onKick
}: MembersListItemProps) {
  const getRoleBadge = (role: GroupRole) => {
    switch (role) {
      case "admin":
        return <Badge className="bg-red-500"><ShieldAlert className="h-3 w-3 mr-1" />Admin</Badge>;
      case "moderator":
        return <Badge className="bg-blue-500"><Shield className="h-3 w-3 mr-1" />Moderator</Badge>;
      default:
        return null;
    }
  };

  const canManageMembers = currentUserRole === "admin" || currentUserRole === "moderator";
  const isCurrentUser = member.userId === currentUserId;

  return (
    <Card className={member.isBanned ? "border-destructive" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {member.user && <Avatar userId={member.user.id} avatarId={member.user.avatarId} size="md" />}
            <div>
              <div className="font-medium flex items-center space-x-2">
                <span>{member.user?.username || "Unknown User"}</span>
                {getRoleBadge(member.role)}
                {member.isBanned && <Badge variant="destructive">Banned</Badge>}
                {member.isMuted && <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Muted</Badge>}
              </div>
              <div className="text-sm text-muted-foreground">
                Joined {new Date(member.joinedAt).toLocaleDateString()}
              </div>
            </div>
          </div>

          {canManageMembers && !isCurrentUser && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Admin-only actions */}
                {currentUserRole === "admin" && (
                  <>
                    {member.role !== "admin" && (
                      <DropdownMenuItem onClick={onPromote}>
                        Promote to {member.role === "member" ? "Moderator" : "Admin"}
                      </DropdownMenuItem>
                    )}
                    
                    {member.role !== "member" && (
                      <DropdownMenuItem onClick={onDemote}>
                        Demote to {member.role === "admin" ? "Moderator" : "Member"}
                      </DropdownMenuItem>
                    )}
                    
                    {!member.isBanned ? (
                      <DropdownMenuItem 
                        onClick={onBan}
                        className="text-destructive"
                      >
                        Ban Member
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={onUnban}>
                        Unban Member
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}

                {/* Moderator and Admin actions */}
                {(currentUserRole === "admin" || 
                  (currentUserRole === "moderator" && member.role === "member")) && (
                  <>
                    {!member.isMuted ? (
                      <DropdownMenuItem onClick={onMute}>
                        Mute for 1 hour
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={onUnmute}>
                        Unmute
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuItem 
                      onClick={onKick}
                      className="text-destructive"
                    >
                      Kick from Group
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
